import { formatSalesOrderNumber } from '@order-management/format';
import { userHasPermissionToViewDeviceDetails } from '@order-management/roles';
import { Device } from '@order-management/types';
import { useCallback, useMemo } from 'react';
import { Element, Icon } from 'react-bulma-components';
import { FaCheck } from 'react-icons/fa6';
import { PiWarningFill } from 'react-icons/pi';
import Conditional from '../../../shared/conditional/conditional';
import useIsCustomerUser from '../../../shared/hooks/is-customer-user';
import useSalesOrderPermissions from '../../../shared/hooks/sales-order-permissions';
import useUser from '../../../shared/hooks/user';
import Link from '../../../ui/link/link';
import PaginatedTable, {
  CustomPaginatedTableProps,
} from '../../../ui/table/paginated-table/paginated-table';
import { Column, RowSelection } from '../../../ui/table/types';
import { getAssignedSoftware, getDeviceName } from '../../utils';
import styles from './device-group-table.module.scss';

type TableProps = CustomPaginatedTableProps<Device>;

export type DeviceGroupTableProps = TableProps & {
  selectedSoftware?: string[];
  showCompleted?: boolean;
  onManageDeviceClick?: (devices: Device[], add: boolean) => void;
  selectedDevices?: Device[];
  disableSelection?: boolean;
  ignoreSalesOrderPermissions?: boolean;
};

export function DeviceGroupTable({
  dataSource: devices,
  selectedSoftware = [],
  isInvalidSelection = () => false,
  showCompleted = false,
  onManageDeviceClick,
  selectedDevices,
  config,
  onChange,
  empty,
  disableSelection,
  ignoreSalesOrderPermissions = false,
  ...props
}: DeviceGroupTableProps) {
  const { user } = useUser();
  const isCustomerUser = useIsCustomerUser();
  const {
    userHasSalesOrderManagePermissions,
    userIsJustViewOnlySalesOrderManager,
  } = useSalesOrderPermissions();

  const isDisabledRow = useCallback(
    (row: Device) => {
      if (ignoreSalesOrderPermissions) {
        return false;
      }

      if (!userIsJustViewOnlySalesOrderManager) {
        const hasManagerPermissions = userHasSalesOrderManagePermissions(
          row.salesOrderLine.salesOrder.id
        );

        return !hasManagerPermissions;
      }

      return false;
    },
    [
      userHasSalesOrderManagePermissions,
      userIsJustViewOnlySalesOrderManager,
      ignoreSalesOrderPermissions,
    ]
  );

  const columns: Column<Device>[] = useMemo(() => {
    const baseColumns: Column<Device>[] = [
      {
        key: 'serialNumber.number',
        className: 'is-width-20',
        render: (el) => {
          const showError = isInvalidSelection([el]);
          let deviceIdentifier = el.serialNumber?.number;
          if (!deviceIdentifier) {
            deviceIdentifier = el.name ?? el.defaultName;
          }
          return (
            <>
              {showError && (
                <PiWarningFill
                  className="has-text-danger-dark"
                  style={{ verticalAlign: 'text-bottom' }}
                />
              )}
              <Conditional
                condition={userHasPermissionToViewDeviceDetails(
                  user,
                  el.salesOrderLine.salesOrder.id
                )}
              >
                <Conditional.True>
                  <Link to={`/devices/${el.id}`}>{deviceIdentifier}</Link>
                </Conditional.True>
                <Conditional.False>
                  <div>{deviceIdentifier}</div>
                </Conditional.False>
              </Conditional>
              {showCompleted &&
                el.assignments.some(
                  (assignment) => assignment.softwareSeat
                ) && (
                  <Icon className="has-text-success-dark">
                    <FaCheck />
                  </Icon>
                )}
            </>
          );
        },
        title: 'Serial Number',
        sortable: false,
        noWrap: true,
      },
      {
        key: 'salesOrder.number',
        className: 'is-width-15',
        render: (el) => (
          <Conditional condition={isDisabledRow(el)}>
            <Conditional.True>
              <span className="is-underlined">
                {formatSalesOrderNumber(el.salesOrderLine.salesOrder.number)}
              </span>
            </Conditional.True>
            <Conditional.False>
              <Link to={`/sales-orders/${el.salesOrderLine.salesOrder.id}`}>
                {formatSalesOrderNumber(el.salesOrderLine.salesOrder.number)}
              </Link>
            </Conditional.False>
          </Conditional>
        ),
        title: 'Sales order',
        sortable: true,
      },
      {
        key: 'name',
        className: ignoreSalesOrderPermissions ? 'is-width-20' : 'is-width-15',
        render: (el) => getDeviceName(el),
        title: 'Device name',
        sortable: false,
      },
      {
        key: 'model.name',
        className: ignoreSalesOrderPermissions ? 'is-width-20' : 'is-width-15',
        render: (el) => el.model.displayName,
        title: 'Device type',
        noWrap: true,
      },
    ];

    if (!ignoreSalesOrderPermissions) {
      baseColumns.push({
        key: 'salesOrder.endUser',
        className: 'is-width-15',
        render: (el) => el.salesOrderLine.salesOrder.endUser,
        title: 'End user',
        sortable: false,
      });
    }

    baseColumns.push({
      key: 'software',
      className: ignoreSalesOrderPermissions ? 'is-width-20' : 'is-width-25',
      render: (el) => (
        <Element className={styles.softwareContainer}>
          {getAssignedSoftware(el.assignments, selectedSoftware)}
        </Element>
      ),
      title: 'Assigned software',
    });
    return baseColumns;
  }, [
    ignoreSalesOrderPermissions,
    isDisabledRow,
    isInvalidSelection,
    selectedSoftware,
    showCompleted,
    user,
  ]);

  const rowSelection: RowSelection | undefined = useMemo(() => {
    if (disableSelection) return;
    let hasDevicesManagerPermission = true;
    if (isCustomerUser && !userIsJustViewOnlySalesOrderManager) {
      hasDevicesManagerPermission = devices.some((device) =>
        userHasSalesOrderManagePermissions(device.salesOrderLine.salesOrder.id)
      );
    }
    if (selectedDevices && onManageDeviceClick && hasDevicesManagerPermission) {
      return {
        onChange: (selectedRowIds) => {
          selectedRowIds.forEach((id) => {
            const foundRow = selectedDevices.find((sd) => sd.id === id);

            if (foundRow) {
              onManageDeviceClick([foundRow], false);
            } else {
              const newSelectedDevice =
                devices.find((device) => device.id === id) || ({} as Device);
              onManageDeviceClick([newSelectedDevice], true);
            }
          });
        },
        selectedRowKeys: devices
          .filter((device) =>
            selectedDevices
              .map((selectedDevice) => selectedDevice.id)
              .includes(device.id)
          )
          .map((d) => d.id),
      };
    }
  }, [
    disableSelection,
    isCustomerUser,
    userIsJustViewOnlySalesOrderManager,
    selectedDevices,
    onManageDeviceClick,
    devices,
    userHasSalesOrderManagePermissions,
  ]);

  return (
    <PaginatedTable
      bordered={false}
      showHeaderDivider
      columns={columns}
      rowKey="id"
      config={config}
      dataSource={devices}
      rowSelection={rowSelection}
      rowLabel={(el) => el.serialNumber?.number ?? el.name ?? el.defaultName}
      empty={empty}
      isInvalidSelection={isInvalidSelection}
      isDisabledSelection={isDisabledRow}
      onChange={onChange}
      {...props}
    />
  );
}

export default DeviceGroupTable;
